<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  dynamicTime,
  dateFormat,
  shortTimestamp,
} from 'shared/helpers/timeHelper';

// Accept Unix seconds and the Date prop contract; missing activity stays missing.
const timestamp = value => {
  if (value === null || value === undefined || value === '' ||
      (typeof value === 'string' && !value.trim())) return null;
  const seconds = value instanceof Date ? value.getTime() / 1000 : Number(value);
  return Number.isFinite(seconds) && seconds > 0 &&
    Number.isFinite(new Date(seconds * 1000).getTime()) ? seconds : null;
};
const relativeTime = value => {
  const seconds = timestamp(value);
  return seconds === null ? '—' : dynamicTime(seconds);
};

export default {
  name: 'TimeAgo',
  props: {
    activityOnly: { type: Boolean, default: false },
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    conversationId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: relativeTime(this.lastActivityTimestamp),
      createdAtTimeAgo: relativeTime(this.createdAtTimestamp),
      timer: null,
    };
  },
  computed: {
    lastActivityTime() {
      if (timestamp(this.lastActivityTimestamp) === null) return '—';
      return shortTimestamp(this.lastActivityAtTimeAgo);
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo);
    },
    createdAt() {
      if (timestamp(this.createdAtTimestamp) === null) return '—';
      const createdTimeDiff = Date.now() - timestamp(this.createdAtTimestamp) * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${
            this.createdAtTimeAgo
          }`
        : `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST')} ${dateFormat(
            timestamp(this.createdAtTimestamp)
          )}`;
    },
    lastActivity() {
      if (timestamp(this.lastActivityTimestamp) === null) return '—';
      const lastActivityTimeDiff =
        Date.now() - timestamp(this.lastActivityTimestamp) * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${
            this.lastActivityAtTimeAgo
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${dateFormat(timestamp(this.lastActivityTimestamp))}`;
    },
    tooltipText() {
      if (this.activityOnly) return timestamp(this.lastActivityTimestamp) !== null ? this.lastActivity : '—';
      return `${this.createdAt}
              ${this.lastActivity}`;
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = relativeTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = relativeTime(this.createdAtTimestamp);
    },
    conversationId() {
      // Reset display values and timer when the row is recycled to a different conversation.
      this.lastActivityAtTimeAgo = relativeTime(this.lastActivityTimestamp);
      this.createdAtTimeAgo = relativeTime(this.createdAtTimestamp);
      if (this.isAutoRefreshEnabled) {
        clearTimeout(this.timer);
        this.createTimer();
      }
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = relativeTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = relativeTime(this.createdAtTimestamp);
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - timestamp(this.lastActivityTimestamp) * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ activityOnly ? lastActivityTime : `${createdAtTime} • ${lastActivityTime}` }}</span>
  </div>
</template>
